/**
 * BOSS Reports — every mutation the parity grid makes to a Perspective viewer config, as pure
 * functions (docs/BossReportsGridParityContract.md §2).
 *
 * The field bar, the grid context menu and the page all speak this vocabulary and nothing else:
 * they hand a config in, get a NEW config back, and pass it to the page's ordinary
 * `handleConfigChange`. Nothing here reaches React, Perspective or the server — which is why the
 * rules that a UI is tempted to enforce per-control (a derived column cannot be grouped on, a
 * measure's aggregate is meaningless once it leaves Values, a raw timestamp must not become the
 * Column area) live here instead, once.
 *
 * The config is Perspective's own `save()` shape, mirrored server-side by
 * `BossDefaultLayout.Build`:
 *
 *   columns        string[]                       the Values area, in order
 *   group_by       string[]                       the Rows area
 *   split_by       string[]                       the Columns area
 *   aggregates     { [col]: 'sum' | ['weighted mean', [denominator]] }
 *   expressions    { [col]: string }              MARGIN/ROI, and the auto-year column (§2.1)
 *   sort           [column, direction][]
 *   filter         [column, operator, value][]
 *   columns_config { [col]: { number_format?, column_size_override?, date_format? } }
 *
 * Every reader below narrows out of `unknown` rather than casting, because a stored layout is
 * whatever a previous version of the viewer wrote and one bad key must not throw on a page that
 * is only trying to draw a field bar.
 */
import type { BossDataType, BossDatePartColumn, BossRunResult } from '../types/boss'

// ── Vocabulary ────────────────────────────────────────────────────────

/** Where a field sits. 'available' means "on the report but not on the grid". */
export type BossFieldArea = 'group_by' | 'split_by' | 'columns' | 'available'

/** Perspective aggregate names this UI offers, in menu order. */
export const BOSS_AGGREGATES = [
    'sum', 'count', 'distinct count', 'avg', 'min', 'max', 'median',
    'first', 'last', 'any', 'var', 'stddev',
] as const
export type BossAggregate = typeof BOSS_AGGREGATES[number]

/** Legacy "Format To" options (C8/S3). 'auto' removes any override. */
export type BossFormat = 'auto' | 'decimal' | 'currency' | 'percent'

/** One field as the field bar shows it. */
export type BossGridField = {
    name: string
    area: BossFieldArea
    /** Position within its area; -1 for 'available'. */
    index: number
    dataType: BossDataType
    /** A MARGIN/ROI expression column: it may sit in Values but never in Row/Column. */
    isDerived: boolean
    /** The effective aggregate when area === 'columns'; null elsewhere. */
    aggregate: BossAggregate | null
    /** The `number_format.style` currently set, or 'auto'. */
    format: BossFormat
}

export type ViewerConfig = Record<string, unknown>

/** The three config keys that are areas of the grid, in field-bar order. */
const PLACED_AREAS = ['group_by', 'split_by', 'columns'] as const

const AGGREGATE_SET: ReadonlySet<string> = new Set<string>(BOSS_AGGREGATES)

/**
 * The exact `number_format` shapes the server already writes
 * (`BossDefaultLayout.PercentNumberFormat` / `CurrencyNumberFormat` / `DecimalNumberFormat`), so a
 * format set in the browser and one imported from a legacy layout are indistinguishable in the
 * stored config — which is what lets `formatOf` read either back.
 */
const NUMBER_FORMATS: Record<Exclude<BossFormat, 'auto'>, Record<string, unknown>> = {
    // `useGrouping: false` is what makes "Numeric" the RAW number. The legacy grid's bare `Numeric`
    // format drew no thousands separator, and the columns it lands on are largely IDENTIFIERS —
    // order numbers, UPCs, item codes — where a comma is not a nicety but a falsehood: 60,626,000
    // is not an order number. Intl groups by default, so it has to be switched off explicitly.
    //
    // The fraction digits are explicit for the same kind of reason. Perspective does not fall back
    // to Intl's own defaults for a key we leave out: it merges what we give it over the datagrid's
    // float default of two fixed places, so `{ style: 'decimal' }` alone drew 6 as "6.00" and 0 as
    // "0.00" (measured). "Numeric" is meant to force nothing — 1.1 reads 1.1 and 6 reads 6 — so the
    // minimum is pinned at 0 and the maximum at Intl's own decimal default of 3.
    decimal: { style: 'decimal', useGrouping: false, minimumFractionDigits: 0, maximumFractionDigits: 3 },
    // `narrowSymbol` is what makes this a DOLLAR SIGN. Intl's default for a currency is the
    // locale-disambiguated symbol, and to an en-US browser CAD is "CA$" — so a Canadian retailer's
    // own figures read CA$182.72, as though they were the foreign ones. The two fixed decimals are
    // Intl's default for a currency and are what money is written with; they are left implicit
    // nowhere else, so they are stated here too.
    currency: {
        style: 'currency', currency: 'CAD', currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
    percent: { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 },
}

// ── The auto-year expression (contract §2.1) ──────────────────────────

const AUTO_YEAR_SUFFIX = ' (Year)'

/** The expression column a date field becomes in the Column area. */
const autoYearColumn = (field: string): string => `${field}${AUTO_YEAR_SUFFIX}`

/** Its Perspective expression. Year is the only bucket that ships with rank 1 (§2.1). */
const autoYearExpression = (field: string): string => `bucket("${field}", 'Y')`

/**
 * True when this expression entry is the auto-year ALIAS of a date field rather than a column in
 * its own right. Those resolve back to the date field they bucket, so they must never be listed as
 * calculated fields or seeded into the field bar under their own name.
 */
const isAutoYearAlias = (name: string, expression: unknown): boolean =>
    name.endsWith(AUTO_YEAR_SUFFIX)
    && expression === autoYearExpression(name.slice(0, -AUTO_YEAR_SUFFIX.length))

// ── Date parts ────────────────────────────────────────────────────────

/**
 * The column type one date part yields.
 *
 * A `bucket` truncates and hands back a datetime, so it still sorts and formats as a date. The
 * three extractions do not: `hour_of_day` is a number, `day_of_week` and `month_of_year` are names.
 *
 * This drives the field bar's classification — which chip reads as a measure and which as a
 * dimension — and nothing else. In particular it does NOT gate the auto-year bucketing in
 * `moveField`, which reads the RUN's schema, and a date-part column is never in that: it is
 * computed in the browser and the SQL result has no entry for it. A part is therefore never
 * double-bucketed today, but that is a property of where `moveField` looks, not of this table.
 */
const DATE_PART_TYPES: Record<BossDatePartColumn['part'], BossDataType> = {
    year: 'datetime',
    monthStart: 'datetime',
    month: 'string',
    week: 'datetime',
    date: 'datetime',
    dayOfWeek: 'string',
    hourStart: 'datetime',
    hour: 'integer',
    minute: 'datetime',
    second: 'datetime',
}

const datePartType = (part: BossDatePartColumn['part']): BossDataType =>
    DATE_PART_TYPES[part] ?? 'string'

/**
 * A layout carrying the expression for every date part the report offers.
 *
 * The part lives on the field DEFINITION, but the thing that computes it is a Perspective
 * expression in the LAYOUT — so a report whose definition gained a part after its layout was saved
 * would otherwise offer a column with nothing behind it. Applied on the way out of the envelope,
 * beside `withDefaultRowLayout`, so it covers an imported layout, an old saved one and one the
 * editor has just written alike.
 *
 * Only ADDS. An expression already present is left alone, even if it differs: a designer who edited
 * the formula by hand meant it, and overwriting it on every read would make that edit unsavable.
 * Returns the config by identity when there is nothing to add.
 */
export function withDatePartExpressions(
    config: ViewerConfig, dateParts: readonly BossDatePartColumn[] | undefined,
): ViewerConfig {
    if (!dateParts || dateParts.length === 0) return config

    const expressions = recordAt(config, 'expressions')
    const missing = dateParts.filter(column => !has(expressions, column.name))
    if (missing.length === 0) return config

    const next: ViewerConfig = { ...config, expressions: { ...expressions } }
    const map = next.expressions as Record<string, unknown>
    for (const column of missing) map[column.name] = column.expression
    return next
}

// ── Calculated fields (Perspective expressions) ───────────────────────

/**
 * Ceilings mirrored from `BossDefaultLayout` so the editor refuses what the layout save would
 * refuse. A 400 from the server after a designer has typed a formula is a worse way to learn this.
 */
export const MAX_CALCULATED_FIELDS = 64
export const MAX_EXPRESSION_LENGTH = 4000

/** One calculated field as the editor lists it. */
export type BossCalculatedField = { name: string; expression: string }

// ── Narrowing helpers ─────────────────────────────────────────────────

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

const has = (map: Record<string, unknown>, key: string): boolean =>
    Object.prototype.hasOwnProperty.call(map, key)

/**
 * One of the area arrays, as names.
 *
 * Non-string entries are dropped rather than preserved. Perspective parks nulls in a column list
 * as empty slots — the server's validator tolerates them for that reason — but they are a chart
 * plugin concept and mean nothing to the Datagrid this report ships, while keeping them would make
 * every drop index ambiguous.
 */
const stringsAt = (config: ViewerConfig, key: string): string[] => {
    const raw = config[key]
    return Array.isArray(raw) ? raw.filter((value): value is string => typeof value === 'string') : []
}

/** One of the object-valued keys, for reading only. Never handed out for mutation. */
const recordAt = (config: ViewerConfig, key: string): Record<string, unknown> => {
    const raw = config[key]
    return isRecord(raw) ? raw : {}
}

/** `sort` / `filter`: an array of clauses, each an array whose head is the column. */
const clausesAt = (config: ViewerConfig, key: string): unknown[][] => {
    const raw = config[key]
    return Array.isArray(raw) ? raw.filter((value): value is unknown[] => Array.isArray(value)) : []
}

const clauseHead = (clause: unknown[]): string | null =>
    typeof clause[0] === 'string' ? clause[0] : null

const isBossAggregate = (value: unknown): value is BossAggregate =>
    typeof value === 'string' && AGGREGATE_SET.has(value)

/**
 * Writes a rebuilt map back onto a config without inventing keys: an empty map is only written
 * when the source already carried that key. A config that never had `expressions` should not grow
 * one, because the adapter restores on any change to the config's layout shape.
 */
const assignMap = (
    next: ViewerConfig, source: ViewerConfig, key: string, map: Record<string, unknown>,
): void => {
    if (Object.keys(map).length > 0 || has(source, key)) next[key] = map
}

const insertAt = (list: string[], value: string, index?: number): string[] => {
    // "Append when index is undefined or past the end"; a negative index is a drop before the
    // first chip, which is the only sensible reading of a drag to the left edge of a zone.
    const at = index === undefined || index > list.length ? list.length : Math.max(0, index)
    const next = list.slice()
    next.splice(at, 0, value)
    return next
}

type ResolvedField = { name: string; dataType: BossDataType }

/**
 * The report field a config entry names, or null when the run result no longer has it.
 *
 * An auto-year expression column resolves to the date field it buckets, so the field bar shows
 * TRANSTIME rather than `TRANSTIME (Year)` and `moveField` is called with the user's own field
 * name. The expression text must match too: a report that genuinely returns a column called
 * `FOO (Year)` is its own field, not an alias.
 *
 * KNOWN GAP, pre-dating date parts and deliberately not widened here: an alias sitting in
 * `group_by` or `columns` — which §2.1 calls legitimate for a hand-built layout — is folded back
 * too, and `moveField` only ever evicts one from `split_by`, so that entry is a chip labelled
 * TRANSTIME that no drag can dislodge. Date parts do not create such leftovers (the editor prunes
 * a part's column when it is unticked or its source hidden, from the stored definition as well as
 * the run), and closing it properly means deciding whether such an entry is a column in its own
 * right — a change to §2.1's semantics that belongs with the grid parity work, not here.
 */
function resolveField(
    entry: string, known: Map<string, BossDataType>, expressions: Record<string, unknown>,
): ResolvedField | null {
    const direct = known.get(entry)
    if (direct !== undefined) return { name: entry, dataType: direct }

    if (!entry.endsWith(AUTO_YEAR_SUFFIX)) return null
    const field = entry.slice(0, -AUTO_YEAR_SUFFIX.length)
    const dataType = known.get(field)
    if (dataType === undefined || expressions[entry] !== autoYearExpression(field)) return null
    return { name: field, dataType }
}

// ── The operations ────────────────────────────────────────────────────

/**
 * Every field the grid could show, in field-bar order: group_by, then split_by, then columns, then
 * available (alphabetical). Derived columns come from `result.derived`; the rest from
 * `result.schema`. A name in the config that the result no longer has is dropped.
 */
export function inventory(config: ViewerConfig, result: BossRunResult): BossGridField[] {
    const derivedNames = new Set(result.derived.map(column => column.name))

    const expressions = recordAt(config, 'expressions')

    const known = new Map<string, BossDataType>(Object.entries(result.schema))
    // A derived column is computed by a Perspective expression over the measures, so the SQL
    // result carries no schema entry for it. MARGIN and ROI are both ratios.
    //
    // It counts as a column of this grid only when the LAYOUT actually defines its expression.
    // `moveField` decides "is this derived, and therefore illegal in Rows/Columns" from
    // `name in expressions`, which is the only signal a bare config carries. Seeding from
    // `result.derived` alone let the two disagree: a report whose layout had lost the MARGIN
    // expression still drew a MARGIN chip, and that chip could then be dragged into Rows — the
    // exact move the rule exists to refuse. One source of truth, so they cannot drift apart.
    //
    // A calculated field a designer added is the same thing without a `result.derived` entry to
    // announce it, so both are seeded the same way: every expression the layout defines that is
    // not an auto-year alias is a column of this grid, typed float because an expression's result
    // type is the engine's to decide and float is the only one the field bar treats as a measure.
    // The report's date-part columns come FIRST, ahead of the expression seeding below, for two
    // reasons. They are typed from the part — a bucket keeps the source's datetime, an extraction
    // yields a name or a number — and the loops below would otherwise claim them as anonymous
    // 'float' calculated fields, since a date part IS an expression in the saved layout.
    //
    // Seeding them here is also what keeps them apart from the auto-year alias: the two have
    // literally the same column name and the same expression, so `resolveField` folds
    // `TRANSTIME (Year)` back into TRANSTIME when it is a drag-into-Columns alias, and finds it
    // here as a column in its own right when the field DECLARES the part.
    const datePartNames = new Set((result.dateParts ?? []).map(column => column.name))
    for (const column of result.dateParts ?? []) known.set(column.name, datePartType(column.part))

    const calculated = calculatedFieldNames(config)
    for (const name of derivedNames) if (!known.has(name) && has(expressions, name)) known.set(name, 'float')
    for (const name of calculated) if (!known.has(name)) known.set(name, 'float')
    const seen = new Set<string>()
    const fields: BossGridField[] = []

    for (const area of PLACED_AREAS) {
        const entries = stringsAt(config, area)
        for (let index = 0; index < entries.length; index++) {
            const entry = entries[index]
            const resolved = resolveField(entry, known, expressions)
            // A field placed in two areas at once is shown in the first one, so the bar never
            // renders two chips the user cannot tell apart.
            if (resolved === null || seen.has(resolved.name)) continue
            seen.add(resolved.name)
            fields.push({
                name: resolved.name,
                area,
                // The position in the config array, NOT among the surviving chips: the field bar
                // reports this index straight back to moveField, which indexes the same array.
                index,
                dataType: resolved.dataType,
                isDerived: (derivedNames.has(resolved.name) || calculated.has(resolved.name))
                    && !datePartNames.has(resolved.name),
                aggregate: area === 'columns' ? aggregateOf(config, entry) : null,
                format: formatOf(config, entry),
            })
        }
    }

    const available = [...known.keys()].filter(name => !seen.has(name)).sort((a, b) => a.localeCompare(b))
    for (const name of available) {
        fields.push({
            name,
            area: 'available',
            index: -1,
            dataType: known.get(name) ?? 'string',
            isDerived: (derivedNames.has(name) || calculated.has(name)) && !datePartNames.has(name),
            aggregate: null,
            format: formatOf(config, name),
        })
    }

    return fields
}

/**
 * The aggregate the picker would show as selected for one Values column.
 *
 * `null` means "Perspective's default", which cannot be computed here — the engine picks it from
 * the column's type — and also covers the `['weighted mean', [denominator]]` entry the server
 * writes for MARGIN/ROI (C16). That one is not on the picker's menu and must never be replaced by
 * a plain aggregate, so reading it back as "no explicit choice" is the safe answer.
 */
function aggregateOf(config: ViewerConfig, column: string): BossAggregate | null {
    const raw = recordAt(config, 'aggregates')[column]
    return isBossAggregate(raw) ? raw : null
}

/**
 * Move `name` to `area` at `index` (append when index is undefined or past the end), removing it
 * from wherever it was. Returns a NEW config; never mutates.
 *
 * Rules, all enforced here rather than in the UI:
 *  - A derived column may only be in 'columns' or 'available'; a move to group_by/split_by returns
 *    the config unchanged.
 *  - Moving OUT of 'columns' drops that column's `aggregates` entry.
 *  - Moving INTO 'columns' sets no aggregate (Perspective's default is right for most columns);
 *    a derived column keeps whatever weighted-mean entry it already had.
 *  - Moving a date/datetime field INTO 'split_by' also creates the auto-year expression (§2.1),
 *    because a raw timestamp there produces one column per distinct instant. Moving it out again
 *    removes that expression if nothing else references it.
 *  - `sort` entries naming a column that leaves the grid are dropped.
 *
 * `schema` is what makes the auto-year rule fire; without it a date field lands in the Column area
 * raw, which is a worse grid but never a broken one.
 */
export function moveField(
    config: ViewerConfig, name: string, area: BossFieldArea, index?: number,
    schema?: Record<string, BossDataType>, groupable?: ReadonlySet<string>,
): ViewerConfig {
    const expressions = { ...recordAt(config, 'expressions') }

    // An expression column computed from already-aggregated measures has no values to group or
    // split on — a MARGIN in group_by yields an empty tree (gap analysis C16). The refusal returns
    // the SAME object rather than a copy, so a caller that compares identity can tell a rejected
    // drag from a real move and leave the session clean.
    //
    // NOT every expression, though. `groupable` names the ones computed per ROW rather than per
    // aggregate — the report's date parts, whose whole purpose is to be grouped on, and which are
    // the same kind of thing as the auto-year alias this function itself puts into split_by below.
    const isDerived = has(expressions, name) && !(groupable?.has(name) ?? false)
    if (isDerived && (area === 'group_by' || area === 'split_by')) return config

    const alias = autoYearColumn(name)

    // The report already OFFERS this date field bucketed to the year, as a declared date part. The
    // auto-year alias below would build a second column under that same name — same expression,
    // same caption — and the two would then sit in different areas at once, where the field bar
    // draws only the first and the second is an entry nobody can see or remove.
    //
    // There is nothing to disambiguate, because they are the same column: the drop is redirected to
    // the declared one, which moves out of wherever it was rather than being duplicated. §2.1's
    // purpose is served either way — what lands in the Column area is the bucketed year, never the
    // raw timestamp.
    if (area === 'split_by' && (groupable?.has(alias) ?? false)) {
        return moveField(config, alias, area, index, schema, groupable)
    }
    // Is the year column this field's DECLARED date part rather than a drag-created alias? They are
    // byte-identical — same name, same expression — so nothing else can tell them apart, and
    // everything below that tidies an alias away has to leave a declared part alone. The part
    // belongs to the report's definition; moving the field it is computed from is not a gesture
    // aimed at it, and deleting its expression here would leave the field bar offering a chip with
    // nothing behind it and the next drag of that chip breaking the whole config.
    const aliasIsDeclared = groupable?.has(alias) ?? false
    const hasAlias = !aliasIsDeclared && expressions[alias] === autoYearExpression(name)

    const groupBy = stringsAt(config, 'group_by')
    const splitBy = stringsAt(config, 'split_by')
    const columns = stringsAt(config, 'columns')
    const beforeGrid = new Set([...groupBy, ...splitBy, ...columns])
    const wasMeasure = columns.includes(name)

    let nextGroupBy = groupBy.filter(column => column !== name)
    let nextColumns = columns.filter(column => column !== name)
    // The auto-year column is taken out of split_by ONLY. §2.1 makes a `columns` or `group_by`
    // entry naming it a reason to KEEP the expression alive, so those two areas are deliberately
    // not this move's business — a hand-built layout may legitimately use the bucketed column as
    // a measure or a row field of its own.
    //
    // A DECLARED date part is never evicted from split_by either: it is a column of the report in
    // its own right, and the user moving the field it reads has said nothing about where it sits.
    let nextSplitBy = splitBy.filter(
        column => column !== name && !(column === alias && !aliasIsDeclared))

    if (area === 'group_by') {
        nextGroupBy = insertAt(nextGroupBy, name, index)
    } else if (area === 'columns') {
        nextColumns = insertAt(nextColumns, name, index)
    } else if (area === 'split_by') {
        // §2.1 (the one piece of gap-analysis rank 8 that ships with rank 1): a raw timestamp in
        // the Column area produces one column per distinct instant and the grid is unusable, so a
        // date/datetime field is bucketed to the year and the expression column goes in instead of
        // the field. Every other interval stays out of scope.
        const dataType = schema?.[name]
        if (dataType === 'date' || dataType === 'datetime') {
            expressions[alias] = autoYearExpression(name)
            nextSplitBy = insertAt(nextSplitBy, alias, index)
        } else {
            nextSplitBy = insertAt(nextSplitBy, name, index)
        }
    }

    const aggregates = { ...recordAt(config, 'aggregates') }
    // An aggregate keyed by a column that is no longer a measure is dead config, and the server's
    // layout validator rejects an `aggregates` key it cannot match to a column of the report.
    // A derived column is the exception: its ['weighted mean', [denominator]] entry is what makes
    // MARGIN/ROI correct at every level (C16), the picker never offers it, and dropping it on a
    // trip out to Available and back would silently change the numbers.
    if (wasMeasure && area !== 'columns' && !isDerived) delete aggregates[name]

    const afterGrid = new Set([...nextGroupBy, ...nextSplitBy, ...nextColumns])
    // Only a column that was on the grid and no longer is gets its sort dropped. A sort naming a
    // column that was never placed is somebody's deliberate hidden sort key (the legacy SORTORDER
    // trick, C17) and is left alone.
    const sort = clausesAt(config, 'sort').filter(clause => {
        const head = clauseHead(clause)
        return head === null || !beforeGrid.has(head) || afterGrid.has(head)
    })

    const columnsConfig = { ...recordAt(config, 'columns_config') }
    if (hasAlias && !nextSplitBy.includes(alias)) {
        // §2.1: the auto-year expression only goes when nothing else names it. A `filter` clause is
        // the case that really bites — deleting the expression under a live filter would leave the
        // config filtering on a column that no longer exists. (A `sort` naming it survives this far
        // only when the prune above kept it, i.e. the column was never on the grid.)
        const referenced =
            nextColumns.includes(alias) ||
            nextGroupBy.includes(alias) ||
            sort.some(clause => clauseHead(clause) === alias) ||
            clausesAt(config, 'filter').some(clause => clauseHead(clause) === alias)

        if (!referenced) {
            delete expressions[alias]
            // Both would then key a column that no longer exists, which the server's layout
            // validator rejects outright (BossDefaultLayout.Validate rule 5).
            delete aggregates[alias]
            delete columnsConfig[alias]
        }
    }

    const next: ViewerConfig = {
        ...config,
        group_by: nextGroupBy,
        split_by: nextSplitBy,
        columns: nextColumns,
    }
    assignMap(next, config, 'expressions', expressions)
    assignMap(next, config, 'aggregates', aggregates)
    assignMap(next, config, 'columns_config', columnsConfig)
    // `has`, not Array.isArray, for the same reason assignMap uses it: a stored config carrying a
    // `sort` of some other shape is normalised to a real array rather than left as garbage.
    if (has(config, 'sort') || sort.length > 0) next.sort = sort
    return next
}

/** Sets or clears one Values column's aggregate. `null` restores Perspective's default. */
export function setAggregate(
    config: ViewerConfig, name: string, aggregate: BossAggregate | null,
): ViewerConfig {
    const aggregates = { ...recordAt(config, 'aggregates') }
    // Perspective's default is the ABSENCE of a key, not a name we could write: the engine picks
    // sum for numerics and count for everything else.
    if (aggregate === null) delete aggregates[name]
    else aggregates[name] = aggregate

    const next: ViewerConfig = { ...config }
    assignMap(next, config, 'aggregates', aggregates)
    return next
}

/**
 * Legacy "Sort By ▸ <measure>" (C7). Replaces the whole `sort` array with a single entry, or
 * clears it for `[Default sorting]`.
 *
 * `columnArea` picks Perspective's direction vocabulary: a field in the COLUMN area orders split
 * columns and takes 'col asc' / 'col desc'; everything else takes 'asc' / 'desc'.
 *
 * DOCUMENTED DIVERGENCE (gap analysis §2 C7, §3): Perspective sorts by the aggregate at EVERY
 * level of the tree. The legacy grid sorted the one field the menu was invoked on — level 1 by
 * RETAIL with level 2 left alphabetical — and there is no engine route to a per-level sort. A
 * user who arranges store-then-brand and sorts by revenue gets both levels by revenue here.
 */
export function setSortBySummary(
    config: ViewerConfig, measure: string | null, direction: 'asc' | 'desc', columnArea: boolean,
): ViewerConfig {
    // 'col asc' / 'col desc' order the SPLIT columns by the measure; the plain directions order the
    // row tree. Same vocabulary the legacy importer writes (BossLegacyLayout).
    const sort = measure === null ? [] : [[measure, columnArea ? `col ${direction}` : direction]]
    return { ...config, sort }
}

/**
 * Legacy "Format To" (C8/S3). Writes `columns_config[column].number_format`:
 *   auto     → delete the number_format (and the whole entry when nothing else is in it)
 *   decimal  → { style: 'decimal', useGrouping: false }
 *   currency → { style: 'currency', currency: 'CAD' }
 *   percent  → { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }
 * These are the exact shapes the server already writes (BossDefaultLayout.PercentNumberFormat).
 *
 * On a group_by field (gap analysis S3) the entry is the same. The viewer drops it on `restore()`
 * unless the field is also in Values, so its row labels are formatted through
 * lib/bossRowHeaderFormats instead.
 */
export function setFormat(config: ViewerConfig, column: string, format: BossFormat): ViewerConfig {
    const columnsConfig = { ...recordAt(config, 'columns_config') }
    const current = columnsConfig[column]
    const entry: Record<string, unknown> = isRecord(current) ? { ...current } : {}

    if (format === 'auto') {
        delete entry.number_format
        // A width the user dragged lives in the same entry under `column_size_override` (C3):
        // "Auto" clears the number format, not the column. Only a wholly empty entry is pruned.
        if (Object.keys(entry).length === 0) delete columnsConfig[column]
        else columnsConfig[column] = entry
    } else {
        entry.number_format = { ...NUMBER_FORMATS[format] }
        columnsConfig[column] = entry
    }

    const next: ViewerConfig = { ...config }
    assignMap(next, config, 'columns_config', columnsConfig)
    return next
}

// ── Calculated fields ─────────────────────────────────────────────────

/**
 * The names of every calculated field the layout defines, auto-year aliases excluded.
 *
 * Exported as a Set because both callers ask membership questions of it. MARGIN and ROI ARE in
 * here: an imported ratio and one a designer wrote are the same object — a Perspective expression
 * in the saved layout — and the editor lists and can delete either.
 */
export function calculatedFieldNames(config: ViewerConfig): Set<string> {
    const expressions = recordAt(config, 'expressions')
    const names = new Set<string>()
    for (const [name, expression] of Object.entries(expressions)) {
        if (!isAutoYearAlias(name, expression)) names.add(name)
    }
    return names
}

/** Every calculated field, name and formula, in insertion order, for the editor's list. */
export function calculatedFields(config: ViewerConfig): BossCalculatedField[] {
    const expressions = recordAt(config, 'expressions')
    const fields: BossCalculatedField[] = []
    for (const [name, expression] of Object.entries(expressions)) {
        if (isAutoYearAlias(name, expression)) continue
        fields.push({ name, expression: typeof expression === 'string' ? expression : '' })
    }
    return fields
}

/**
 * Why this name and formula cannot be saved, or null when they can.
 *
 * `original` is the name being edited, so renaming a field to itself is not a collision. The rules
 * are the ones the layout validator and Perspective between them impose; checking here means the
 * designer is told at the keystroke rather than by a 400 after Save.
 */
export function validateCalculatedField(
    config: ViewerConfig, result: BossRunResult | null,
    name: string, expression: string, original?: string,
): string | null {
    const trimmed = name.trim()
    if (trimmed.length === 0) return 'A calculated field needs a name.'
    if (expression.trim().length === 0) return 'A calculated field needs a formula.'
    if (expression.length > MAX_EXPRESSION_LENGTH)
        return `A formula is at most ${MAX_EXPRESSION_LENGTH} characters.`

    // A name that collides with a real result column would shadow it: the expression would win and
    // the column's own values would silently vanish from the grid.
    if (result && Object.prototype.hasOwnProperty.call(result.schema, trimmed))
        return `'${trimmed}' is already a column of this report.`

    // …and the same for a date-part column, which is a column of the report but lives in
    // `dateParts` rather than in `schema`. The Year part needs this check specifically: its name
    // and expression are the auto-year alias's, so `calculatedFieldNames` filters it out and the
    // collision test below would let a formula be written straight over it. Every other part is
    // caught below; without this the guard is inconsistent between parts of the same field.
    if (result && (result.dateParts ?? []).some(column => column.name === trimmed))
        return `'${trimmed}' is a date part of this report.`

    const existing = calculatedFieldNames(config)
    if (trimmed !== original && existing.has(trimmed))
        return `'${trimmed}' is already a calculated field.`

    if (original === undefined && existing.size >= MAX_CALCULATED_FIELDS)
        return `A report may have at most ${MAX_CALCULATED_FIELDS} calculated fields.`

    return null
}

/**
 * Adds a calculated field, or replaces one under `original` when the editor is editing.
 *
 * The new column is appended to Values, because a field nobody can see is not what "add" means and
 * an expression is a measure — `moveField` refuses to put one in Rows or Columns, exactly as it
 * refuses an imported MARGIN. A rename carries the column's position, aggregate and format with
 * it: the field is the same field, and losing its formatting to a typo correction would be its own
 * small bug.
 */
export function setCalculatedField(
    config: ViewerConfig, name: string, expression: string, original?: string,
): ViewerConfig {
    const target = name.trim()
    const expressions = { ...recordAt(config, 'expressions') }
    const renaming = original !== undefined && original !== target

    if (original !== undefined) delete expressions[original]
    expressions[target] = expression

    const next: ViewerConfig = { ...config }
    assignMap(next, config, 'expressions', expressions)

    if (renaming) {
        // Carry everything keyed by the old name across, then drop it. Areas first, so the column
        // keeps its slot rather than jumping to the end of Values.
        for (const area of PLACED_AREAS) {
            const entries = stringsAt(config, area)
            if (entries.includes(original)) {
                next[area] = entries.map(entry => (entry === original ? target : entry))
            }
        }

        const aggregates = { ...recordAt(config, 'aggregates') }
        if (has(aggregates, original)) {
            aggregates[target] = aggregates[original]
            delete aggregates[original]
        }
        assignMap(next, config, 'aggregates', aggregates)

        const columnsConfig = { ...recordAt(config, 'columns_config') }
        if (has(columnsConfig, original)) {
            columnsConfig[target] = columnsConfig[original]
            delete columnsConfig[original]
        }
        assignMap(next, config, 'columns_config', columnsConfig)

        next.sort = clausesAt(config, 'sort')
            .map(clause => (clauseHead(clause) === original ? [target, ...clause.slice(1)] : clause))
        return next
    }

    if (original === undefined && !stringsAt(config, 'columns').includes(target)) {
        next.columns = [...stringsAt(config, 'columns'), target]
    }
    return next
}

/**
 * True when the layout mentions this column anywhere: an area, an aggregate, a format, a sort, a
 * filter or an expression.
 *
 * Asked before rewriting a layout to drop a column, so hiding a field that the layout never
 * mentioned is not counted as a layout change — a save nobody asked for, over a document somebody
 * else may have edited since, is worse than no save.
 */
export function referencesColumn(config: ViewerConfig, name: string): boolean {
    for (const area of PLACED_AREAS) if (stringsAt(config, area).includes(name)) return true
    for (const key of ['expressions', 'aggregates', 'columns_config']) {
        if (has(recordAt(config, key), name)) return true
    }
    for (const key of ['sort', 'filter']) {
        if (clausesAt(config, key).some(clause => clauseHead(clause) === name)) return true
    }
    return false
}

/**
 * Removes a column from a layout and every reference to it — the areas, the aggregate, the format,
 * the sort and the filter, as well as any expression defining it.
 *
 * Everything, not just the one place: a name left in `columns` that the table cannot resolve is a
 * column Perspective is asked to draw and cannot, and the layout validator refuses the whole save
 * over it. Used for a calculated field the designer deleted and for a field they hid, which from
 * the layout's point of view are the same event — the column stops existing.
 */
export function removeColumn(config: ViewerConfig, name: string): ViewerConfig {
    const expressions = { ...recordAt(config, 'expressions') }
    delete expressions[name]

    const aggregates = { ...recordAt(config, 'aggregates') }
    delete aggregates[name]

    const columnsConfig = { ...recordAt(config, 'columns_config') }
    delete columnsConfig[name]

    const next: ViewerConfig = { ...config }
    assignMap(next, config, 'expressions', expressions)
    assignMap(next, config, 'aggregates', aggregates)
    assignMap(next, config, 'columns_config', columnsConfig)

    for (const area of PLACED_AREAS) {
        const entries = stringsAt(config, area)
        if (entries.includes(name)) next[area] = entries.filter(entry => entry !== name)
    }

    if (has(config, 'sort')) {
        next.sort = clausesAt(config, 'sort').filter(clause => clauseHead(clause) !== name)
    }
    if (has(config, 'filter')) {
        next.filter = clausesAt(config, 'filter').filter(clause => clauseHead(clause) !== name)
    }

    return next
}

/**
 * Removes a calculated field. The same operation as {@link removeColumn} — named separately
 * because the editor's Remove button is about a formula, not about a column of the query.
 */
export const removeCalculatedField = removeColumn

// ── Reconciling a stored layout with the run ──────────────────────────

/**
 * Every column reference in a Perspective expression.
 *
 * The expression language quotes a column with DOUBLE quotes and a string literal with single
 * ones — `bucket("TRANSTIME", 'Y')` is one of each — so every double-quoted run is a column name
 * and nothing else is.
 */
const COLUMN_REFERENCE = /"([^"]*)"/g

const columnReferences = (expression: string): string[] =>
    [...expression.matchAll(COLUMN_REFERENCE)].map(match => match[1])

/**
 * Every column name Perspective could resolve against this run: the SQL result's own columns, the
 * report's date parts, and each expression the layout defines whose formula bottoms out in those.
 *
 * The expressions are settled by fixed point rather than in one pass, because one may be written
 * over another — a designer's MARGIN_PCT over MARGIN — and an expression is only resolvable once
 * everything it names is. A formula naming no column at all (`today()`) resolves immediately; a
 * cycle never does, which is also what Perspective would say about it.
 */
function resolvableColumns(config: ViewerConfig, result: BossRunResult): Set<string> {
    const resolvable = new Set<string>(Object.keys(result.schema))
    // A date part is computed in the browser, so the SQL result has no schema entry for it — but
    // the RUN declares it, and `withDatePartExpressions` has already put its formula in the config.
    for (const column of result.dateParts ?? []) resolvable.add(column.name)

    const pending = new Map<string, string>()
    for (const [name, expression] of Object.entries(recordAt(config, 'expressions'))) {
        if (resolvable.has(name)) continue
        pending.set(name, typeof expression === 'string' ? expression : '')
    }

    let grew = true
    while (grew) {
        grew = false
        for (const [name, expression] of pending) {
            if (!columnReferences(expression).every(reference => resolvable.has(reference))) continue
            resolvable.add(name)
            pending.delete(name)
            grew = true
        }
    }

    return resolvable
}

/**
 * A stored layout with every column the run no longer returns taken out of it.
 *
 * A report's SQL is edited long after its layout was saved, and a layout naming a column the query
 * has since dropped is not a cosmetic problem: `restore()` validates the WHOLE config and rejects
 * all of it over one unknown name —
 *
 *     Error: Unknown column "ADVPASSEMAIL" in field `columns`
 *
 * With `{ suppress_errors: true }` that rejection is only a console warning, so the failure is
 * silent and total: the field bar draws the page's own config and looks like it worked, while the
 * grid keeps drawing whatever it last accepted. Every drag, reorder and row-layout switch for the
 * rest of the session goes into the same rejected restore and appears to do nothing.
 *
 * So the reconciliation happens on the way out of the envelope, beside `withDatePartExpressions`
 * and `withDefaultRowLayout` and for the same reason: what reaches the viewer must describe THIS
 * run. Each doomed name goes through {@link removeColumn}, which is already the operation for "this
 * column stopped existing" — so it leaves the areas, the aggregate, the format, the sort and the
 * filter consistent, and the result stays savable by the server's validator.
 *
 * An expression is pruned on the same test as a plain column, because one whose formula names a
 * departed column is just as unresolvable — and its own dependents go with it.
 *
 * Returns the config by identity when there is nothing to prune, so the viewer adapter, which keys
 * its restore on the config's shape, is never asked to redraw for a no-op.
 */
export function withoutMissingColumns(
    config: ViewerConfig, result: BossRunResult | undefined,
): ViewerConfig {
    if (!result) return config
    // A run that reports no columns at all is a failed or not-yet-arrived result, not a report
    // whose every column was deleted. Pruning against it would strip the layout to nothing — and
    // that stripped layout is what a later Save would write over the stored one.
    if (Object.keys(result.schema).length === 0) return config

    const resolvable = resolvableColumns(config, result)
    const missing = new Set<string>()
    const check = (name: string): void => { if (!resolvable.has(name)) missing.add(name) }

    for (const area of PLACED_AREAS) for (const entry of stringsAt(config, area)) check(entry)
    // The satellites too, not just the areas: the server's layout validator refuses an aggregates
    // or columns_config key it cannot match to a column of the report, so a stale one would make
    // the layout unsavable even after the grid was drawing it again.
    for (const key of ['expressions', 'aggregates', 'columns_config']) {
        for (const name of Object.keys(recordAt(config, key))) check(name)
    }
    for (const key of ['sort', 'filter']) {
        for (const clause of clausesAt(config, key)) {
            const head = clauseHead(clause)
            if (head !== null) check(head)
        }
    }

    if (missing.size === 0) return config

    let next = config
    for (const name of missing) next = removeColumn(next, name)
    return next
}

/** The format currently in force for a column. */
export function formatOf(config: ViewerConfig, column: string): BossFormat {
    const entry = recordAt(config, 'columns_config')[column]
    if (!isRecord(entry)) return 'auto'

    const numberFormat = entry.number_format
    if (!isRecord(numberFormat)) return 'auto'

    const style = numberFormat.style
    return style === 'decimal' || style === 'currency' || style === 'percent' ? style : 'auto'
}

// ── Wall-clock datetimes ──────────────────────────────────────────────

/**
 * A legacy datetime is a WALL CLOCK in the originating store's zone. It carries no offset and is
 * not UTC: a sale rung up at 2:14pm in Invermere is stored as 14:14 and must read as 14:14 for
 * everyone, whatever zone their browser is in.
 *
 * The API honours that by LABELLING the SQL wall clock as UTC — `SpecifyKind`, which touches no
 * ticks — and sending the epoch-ms of that instant (BossColumnMapper.ToJsonValue). So 14:14 crosses
 * the wire as the number for 14:14 UTC. That is only right if the browser formats it in UTC, and
 * Perspective's datetime formatter does not: it builds
 * `Intl.DateTimeFormat(navigator.languages, { timeZone: preset?.timeZone ?? undefined, … })`
 * (viewer column-format.ts:150), and with no preset Intl falls back to the browser's own zone.
 * Nothing in this app ever wrote a preset. Hence 7:14am in Vancouver.
 *
 * This pins the formatter to UTC for every column the run result types `datetime`, so the digits
 * SQL holds are the digits on screen. It is applied as an overlay at render time, never persisted,
 * for the same reason `theme` is: it belongs to how THIS browser draws the report, not to the
 * report — and an overlay reaches every layout that was saved before it existed, with no migration.
 *
 * Why the formatter, and never the value: `date` columns already render through a formatter that
 * hard-codes `timeZone: "utc"` (column-format.ts:216), and the engine's `bucket()` truncates in UTC
 * regardless of host zone — so the as-if-UTC encoding is what keeps a 31-Dec-23:00 row in the year
 * its header says. Shifting the epoch to suit a local formatter would move it into the wrong year
 * while the header agreed: a silently wrong report in place of a visibly wrong cell.
 *
 * Returns the SAME object when there is nothing to add — no datetime columns, or every one already
 * carries a zone — so the viewer adapter, which keys its restore on the config's shape, is never
 * asked to redraw for a no-op. A zone somebody chose deliberately in the viewer's Style tab is
 * left alone.
 */
export function withWallClockDatetimes(
    config: ViewerConfig,
    schema: Record<string, BossDataType> | undefined,
): ViewerConfig {
    if (!schema) return config

    const columnsConfig = { ...recordAt(config, 'columns_config') }
    let changed = false
    for (const [column, dataType] of Object.entries(schema)) {
        if (dataType !== 'datetime') continue

        const current = columnsConfig[column]
        const entry: Record<string, unknown> = isRecord(current) ? { ...current } : {}
        const dateFormat = isRecord(entry.date_format) ? entry.date_format : {}
        if (has(dateFormat, 'timeZone')) continue

        entry.date_format = { ...dateFormat, timeZone: 'UTC' }
        columnsConfig[column] = entry
        changed = true
    }
    if (!changed) return config

    const next: ViewerConfig = { ...config }
    assignMap(next, config, 'columns_config', columnsConfig)
    return next
}

// ── Number-format defaults for layouts saved before they were written ─

/**
 * The keys a stored `number_format` is missing, or null when it needs none.
 *
 * Every key here was once left out on the belief that Intl's own default would apply. It does not:
 * Perspective merges a `number_format` over the DATAGRID's default, which for a float is two fixed
 * decimal places — so a "Numeric" column drew 6 as "6.00", and a bare `{ style: 'decimal' }` from
 * before `useGrouping` was written drew an order number as 60,626,000. Neither is what the format
 * means.
 *
 * Only keys the stored format is SILENT on are supplied. Anything chosen deliberately — in the
 * viewer's Style tab, or by a report that really does want its own precision — is left exactly as
 * it was set.
 */
function missingNumberFormatKeys(numberFormat: Record<string, unknown>): Record<string, unknown> | null {
    const style = numberFormat.style
    if (style !== 'decimal' && style !== 'currency') return null

    const wanted = NUMBER_FORMATS[style]
    const add: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(wanted)) {
        // `style` is what identified the format, and `currency` names the denomination — neither
        // is a default to supply. Percent already shipped with its fraction digits, so it is not
        // in scope here at all.
        if (key === 'style' || key === 'currency') continue
        if (has(numberFormat, key)) continue
        add[key] = value
    }
    return Object.keys(add).length === 0 ? null : add
}

/**
 * Fills in the number-format keys a stored layout predates.
 *
 * `setFormat` writes all of them now (see `NUMBER_FORMATS`), but layouts saved or imported before
 * it did carry only what was written at the time. This is the same kind of overlay as
 * `withWallClockDatetimes` and for the same reason: applied at render time, never persisted, it
 * reaches every one of those layouts without a migration and without rewriting a report the user
 * never asked to change.
 *
 * Returns the SAME object when there is nothing to add, so the viewer adapter, which keys its
 * restore on the config's shape, is never asked to redraw for a no-op.
 */
export function withNumberFormatDefaults(config: ViewerConfig): ViewerConfig {
    const columnsConfig = { ...recordAt(config, 'columns_config') }
    let changed = false
    for (const [column, current] of Object.entries(columnsConfig)) {
        if (!isRecord(current)) continue
        const numberFormat = current.number_format
        if (!isRecord(numberFormat)) continue
        const add = missingNumberFormatKeys(numberFormat)
        if (add === null) continue

        columnsConfig[column] = { ...current, number_format: { ...numberFormat, ...add } }
        changed = true
    }
    if (!changed) return config

    const next: ViewerConfig = { ...config }
    assignMap(next, config, 'columns_config', columnsConfig)
    return next
}

/** True when the config has any group_by — i.e. Expand/Collapse All mean something. */
export function isGrouped(config: ViewerConfig): boolean {
    return stringsAt(config, 'group_by').length > 0
}

// ── Row layout ────────────────────────────────────────────────────────

/**
 * How the Rows area is drawn.
 *
 *   'flat' — one row-header column per group_by field, side by side. What the legacy DevExpress
 *            grid did, so it is what an imported report starts on (`BossLegacyLayout` and
 *            `BossDefaultLayout` both write it). Leaf rows only: no TOTAL row, no sub-total row
 *            per group, which is also how legacy behaved — it had automatic totals switched off.
 *   'tree' — Perspective's default: ONE indented, collapsible drill-down column, plus a TOTAL row
 *            and a sub-total per group. Readable at two levels, unreadable at six.
 *
 * The names are this UI's, not Perspective's: the stored key is `group_rollup_mode`, whose values
 * are 'rollup' (tree) and 'flat'. Its third value, 'total', draws no row headers at all; nothing
 * here writes it, and `rowLayoutOf` reports it as null rather than ticking a menu item that would
 * be a lie about what the grid is showing.
 */
export type BossRowLayout = 'tree' | 'flat'

/** The stored `group_rollup_mode` for each of the two layouts this UI offers. */
const ROLLUP_MODES: Record<BossRowLayout, string> = { tree: 'rollup', flat: 'flat' }

/**
 * The row layout in force, or null when the config carries a mode this menu does not offer.
 *
 * An absent key is 'tree': that is Perspective's own default, so a layout saved before this
 * setting existed reads back as the tree it is actually drawing.
 */
export function rowLayoutOf(config: ViewerConfig): BossRowLayout | null {
    const mode = config.group_rollup_mode
    if (mode === undefined || mode === null || mode === 'rollup') return 'tree'
    if (mode === 'flat') return 'flat'
    return null
}

/**
 * A stored viewer config with the row layout it never named filled in as side by side.
 *
 * Layouts saved before `group_rollup_mode` existed carry no such key, and Perspective's own
 * default for a missing key is the indented tree — so an imported report built for the legacy
 * grid's side-by-side row headers opens as a six-deep drill-down. This puts the default where it
 * belongs: on the value read out of the envelope, before anything renders or compares it.
 *
 * Deliberately NOT in `rowLayoutOf`, which must keep reporting an absent key as the tree — that is
 * what the grid would actually draw, and the menu must not tick a layout the user is not looking
 * at. This runs one step earlier, so by the time `rowLayoutOf` sees the config there is no absent
 * key left to interpret.
 *
 * A config that names a mode — 'rollup' included — is returned unchanged and by identity: a
 * deliberate Tree choice is somebody's saved layout, not a gap to fill.
 *
 * The second thing this closes is subtler. `perspectiveViewer`'s echo guard keys on a shape that
 * includes `group_rollup_mode`; hand the viewer a config missing it and the shape carries
 * undefined while the viewer's echo carries 'rollup', so the two never match, the guard never
 * disarms, and after its timeout the viewer's own echo is accepted as a user edit — which is how
 * a stored layout silently acquires a 'rollup' nobody chose.
 */
export function withDefaultRowLayout(config: ViewerConfig): ViewerConfig {
    const mode = config.group_rollup_mode
    if (mode !== undefined && mode !== null) return config
    return { ...config, group_rollup_mode: ROLLUP_MODES.flat }
}

/** Switches the Rows area between the indented tree and one column per field. */
export function setRowLayout(config: ViewerConfig, layout: BossRowLayout): ViewerConfig {
    // Written explicitly for both, never deleted for 'tree'. Perspective's own `save()` always
    // emits the key, so a config that deleted it would be handed straight back with 'rollup' in
    // place — a config change nobody made, raising Save over a difference the user cannot see.
    return { ...config, group_rollup_mode: ROLLUP_MODES[layout] }
}
